// MA-655 — `two_ten_frames_sub` concept visual for TWO_TEN_FRAMES-AUTHORED
// subtraction (MA-651 §C4 — the founder's ME_11_038 class, ms.math.11).
//
// Renders two stacked ten frames (20 cells): cells 1..a filled, the LAST b
// of those filled cells crossed out — so the make-ten structure 1.OA.C.6
// names stays visible (e.g. 16 − 7 shows a full first frame + 6 in the
// second, with the 7 crossed cells spanning the second frame's 6 and the
// first frame's last 1), instead of collapsing to 11-20 loose dots in
// rows of five that the old take_away arm drew. Layout mirrors the sibling
// two-ten-frame renderers (TwoTenFramesMissingAddend / TwoTenFramesTeen)
// in rendererRegistry.tsx.
//
// MA-647 operand caption above (`a − b`). NO numeral label (unlike
// TwoTenFramesMissingAddend's "= N") — this picture is read-the-frames,
// not count-forward-to-a-shown-total.
//
// ANSWER-SAFETY: NOT answer-safe by construction, same class as take_away
// and ten_frame_sub — the uncrossed cell count IS the answer. Count-free
// aria-label, matching TenFrame/TenFrameSubRenderer's own precedent.
//
// STAGED (see mathVisualStage.ts STAGED_KINDS): unlike ten_frame_sub, this
// kind IS projected to count_back/equation at bridging/fluent — it models a
// take-away (the concrete model of a subtraction fact), not an authored
// make-ten frame the child is meant to read at every band.
//
// ADDITIVE — registers itself under "two_ten_frames_sub" (see
// rendererRegistry.tsx). The mapper (contentToEngineItem.ts, MA-655) emits
// this kind only when op==="-", a−b===total, b>0, the authored
// visualSupport/sourceStimulus_v2.type is "two_ten_frames", and a<=20.
import type { MathRendererProps } from "./rendererRegistry";
import { OperandCaption, operandCaptionSize } from "./StagedMathRenderers";

const DOT_INTACT = "var(--pal-orange, #d97742)";
const DOT_REMOVED_BG = "var(--color-bg-secondary, #f5efe3)";
const DOT_REMOVED_STROKE = "var(--color-border-secondary, #cdbfae)";
const X_COLOR = "var(--color-error, #c0392b)";

const CELL = 68;
const PITCH = 70; // CELL + 2px gap
const COLS = 5;
const PAD = 6;
const FRAME_GAP = 16; // vertical gap between the two ten-frames
const R = 22;
const CAPTION_GAP = 14;

function TwoTenFramesSubRenderer({ a, b }: MathRendererProps) {
  const safeA = Math.max(0, Math.min(20, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 0)));
  const safeB = Math.max(0, Math.min(safeA, Math.floor(Number.isFinite(b ?? NaN) ? (b as number) : 0)));
  const firstIntact = safeA - safeB;

  const gridW = COLS * PITCH + 2 * PAD; // 362
  const gridH = 4 * PITCH + 2 * PAD + FRAME_GAP;
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => {
    const frame = Math.floor(i / 10);
    const rowInFrame = Math.floor((i % 10) / COLS);
    return PAD + frame * (2 * PITCH + FRAME_GAP) + rowInFrame * PITCH;
  };

  const cap = operandCaptionSize();
  const svgW = Math.max(gridW, cap.width);
  const svgH = cap.height + CAPTION_GAP + gridH;
  const gridOffsetX = (svgW - gridW) / 2;
  const capOffsetX = (svgW - cap.width) / 2;
  const gridOffsetY = cap.height + CAPTION_GAP;

  const xArm = (R * 0.6).toFixed(1);

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      width="100%"
      style={{ maxWidth: Math.min(360, svgW) }}
      role="img"
      aria-label="Two ten frames with some counters taken away."
    >
      <OperandCaption a={safeA} b={safeB} op="-" x={capOffsetX} y={0} />
      <g transform={`translate(${gridOffsetX}, ${gridOffsetY})`}>
        {Array.from({ length: 20 }).map((_, i) => {
          const x = cellX(i);
          const y = cellY(i);
          const filled = i < safeA;
          const crossed = filled && i >= firstIntact;
          return (
            <g key={i}>
              <rect x={x} y={y} width={CELL} height={CELL} rx="8" fill="none"
                stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
              {filled && !crossed && (
                <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R} fill={DOT_INTACT} />
              )}
              {crossed && (
                <>
                  <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R}
                    fill={DOT_REMOVED_BG} stroke={DOT_REMOVED_STROKE} strokeWidth="1.5" />
                  <line x1={x + CELL / 2 - +xArm} y1={y + CELL / 2 - +xArm}
                    x2={x + CELL / 2 + +xArm} y2={y + CELL / 2 + +xArm}
                    stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
                  <line x1={x + CELL / 2 + +xArm} y1={y + CELL / 2 - +xArm}
                    x2={x + CELL / 2 - +xArm} y2={y + CELL / 2 + +xArm}
                    stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
                </>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default TwoTenFramesSubRenderer;
